import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Linkedin, Search, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { LEVELS } from "@/data/ambassadorProgramme";

const levelColor = (n) => LEVELS.find((l) => l.name === n)?.color || "#71717a";

const Verify = () => {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("verify") || "");
  const [res, setRes] = useState(null);
  const check = async (c) => {
    if (!c) return;
    try { const { data } = await api.get(`/ambassadors/verify/${encodeURIComponent(c.trim())}`); setRes(data); }
    catch (e) { setRes({ error: formatApiError(e.response?.data?.detail) }); }
  };
  useEffect(() => { if (code) check(code); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="card-warm p-5" data-testid="ambassador-verify">
      <h2 className="font-display text-xl inline-flex items-center gap-2"><ShieldCheck size={18}/> Verify an Ambassador</h2>
      <form onSubmit={(e) => { e.preventDefault(); check(code); }} className="flex gap-2 mt-3">
        <input className="field flex-1 min-w-0" placeholder="CSI-NAME or AMB-00001" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="ambassador-verify-input"/>
        <button className="chip text-sm" data-testid="ambassador-verify-btn"><Search size={14}/></button>
      </form>
      {res && (res.error ? <div className="text-sm text-red-700 mt-2" data-testid="ambassador-verify-result">{res.error}</div> : (
        <div className="text-sm mt-3" data-testid="ambassador-verify-result">
          <b>{res.name}</b> · {res.code} · {res.ambassador_id}<br/>
          Status: <span className={res.status === "active" ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>{res.status === "active" ? "Active CSI Ambassador" : `Not active (${res.status})`}</span>
          {res.since && <span className="text-zinc-500"> · since {res.since.slice(0, 10)}</span>}
        </div>
      ))}
    </div>
  );
};

export default function AmbassadorDirectoryPage() {
  useSeo({ title: "CSI Ambassador Directory", description: "Find and verify official CSI Ambassadors: independent partners promoting CSCE and CSP converged security certifications worldwide, with 5% learner discounts.", path: "/ambassadors/directory" });
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/ambassadors/directory").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="ambassador-directory-page">
      <div className="eyebrow mb-2">CSI Ambassador Programme</div>
      <h1 className="font-display text-4xl sm:text-5xl">Ambassador Directory</h1>
      <p className="text-sm text-zinc-700 mt-3 max-w-2xl">Official CSI Ambassadors who have chosen to be listed. Ambassadors are independent referral partners, not CSI employees. <Link to="/ambassadors" className="text-[var(--gold-deep)] hover:underline">Become an Ambassador</Link></p>
      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4 content-start">
          {rows === null && <div className="text-sm text-zinc-500">Loading…</div>}
          {rows?.length === 0 && <div className="text-sm text-zinc-500" data-testid="ambassador-directory-empty">No ambassadors are listed yet.</div>}
          {rows?.map((a) => (
            <div key={a.code} className="card-warm p-5" data-testid={`directory-card-${a.code}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><div className="font-display text-xl truncate">{a.name}</div><div className="text-xs text-zinc-500">{[a.organisation, a.country].filter(Boolean).join(" · ")}</div></div>
                {a.level !== "Member" && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-white" style={{ background: levelColor(a.level) }}>{a.level}</span>}
              </div>
              <div className="text-xs mt-3 text-zinc-600">Code <b>{a.code}</b> · {a.ambassador_id}</div>
              {a.linkedin && <a href={a.linkedin} target="_blank" rel="noopener noreferrer" className="text-xs inline-flex items-center gap-1 mt-2 text-[var(--gold-deep)] hover:underline"><Linkedin size={12}/> LinkedIn</a>}
            </div>
          ))}
        </div>
        <Verify/>
      </div>
    </div>
  );
}
